import {
  BottomSheet,
  CbhIcon,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { DialogContent, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { useZendeskArticle } from "@src/appV2/Zendesk/useZendeskArticle";
import { useCallback } from "react";

import {
  CONCORDE_PROGRAMS,
  SCHOLARSHIP_BOTTOM_SHEET_GRADIENT,
  SCHOLARSHIP_RESPONSES,
  type ScholarshipResponse,
} from "./constants";

interface ScholarshipBottomSheetProps {
  modalState: UseModalState;
  workerId: string;
  onSubmit: (response: ScholarshipResponse) => void;
}

export function ScholarshipBottomSheet(props: ScholarshipBottomSheetProps) {
  const { modalState, workerId, onSubmit } = props;
  const { openArticle } = useZendeskArticle();
  const { concordeScholarshipTermsSupportLink } = useSupportLinks();

  const handleInterested = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.CONCORDE_SCHOLARSHIP_V2_INTEREST_SUBMITTED, {
      workerId,
      response: SCHOLARSHIP_RESPONSES.INTERESTED,
    });

    onSubmit(SCHOLARSHIP_RESPONSES.INTERESTED);
    modalState.closeModal();
  }, [workerId, onSubmit, modalState]);

  const handleNotInterested = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.CONCORDE_SCHOLARSHIP_V2_INTEREST_SUBMITTED, {
      workerId,
      response: SCHOLARSHIP_RESPONSES.NOT_INTERESTED,
    });

    onSubmit(SCHOLARSHIP_RESPONSES.NOT_INTERESTED);
    modalState.closeModal();
  }, [workerId, onSubmit, modalState]);

  function handleClose(): void {
    modalState.closeModal();
  }

  return (
    <BottomSheet
      modalState={modalState}
      paperSx={{
        background: SCHOLARSHIP_BOTTOM_SHEET_GRADIENT,
        overflow: "hidden",
      }}
      header={
        <BottomSheetHeader
          onClose={() => {
            handleClose();
          }}
        />
      }
      footer={
        <DialogFooter orientation="vertical">
          <Button fullWidth variant="contained" size="large" onClick={handleInterested}>
            Count me in
          </Button>
          <TextButton size="large" sx={{ alignSelf: "center" }} onClick={handleNotInterested}>
            Not Interested
          </TextButton>
        </DialogFooter>
      }
      onClose={() => {
        handleClose();
      }}
    >
      <DialogContent sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
        <Stack gap={6}>
          <Text
            semibold
            variant="h2"
            sx={(theme) => ({ textAlign: "center", color: theme.palette.text.primary })}
          >
            15% Off Tuition
          </Text>

          <Text
            variant="body1"
            sx={(theme) => ({ textAlign: "center", color: theme.palette.text.secondary })}
          >
            15% off tuition at select programs offered either online or at 17 campuses nationwide.
          </Text>
        </Stack>

        <Stack sx={{ marginTop: 8 }} gap={5}>
          <Text semibold variant="h5" sx={{ textAlign: "center" }}>
            Some of the Available Programs
          </Text>
          <List dense disablePadding sx={{ listStyleType: "disc", paddingLeft: 5 }}>
            {CONCORDE_PROGRAMS.map((program) => (
              <ListItem
                key={program}
                disableGutters
                disablePadding
                sx={{ display: "list-item", padding: 0, minHeight: 0 }}
              >
                <Text variant="body1">{program}</Text>
              </ListItem>
            ))}
          </List>
          <TextButton
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.CONCORDE_SCHOLARSHIP_V2_SEE_ALL_PROGRAMS_CLICKED, {
                workerId,
              });
              window.open("https://www.concorde.edu/programs", "_blank");
            }}
          >
            See all programs and learn more
          </TextButton>
          <Text
            variant="caption"
            sx={(theme) => ({
              textAlign: "left",
              color: theme.palette.text.secondary,
              fontStyle: "italic",
            })}
          >
            *The 15% tuition discount applies to the Dental Hygiene program only at the Miramar, FL
            campus.
          </Text>
        </Stack>

        <Stack sx={{ marginTop: 8 }} gap={4}>
          <Text
            semibold
            variant="caption"
            sx={(theme) => ({
              textAlign: "left",
              color: theme.palette.text.secondary,
              letterSpacing: "0.1em",
            })}
          >
            WHAT YOU NEED TO DO
          </Text>
          <List dense disablePadding>
            <ListItem disableGutters sx={{ alignItems: "flex-start", paddingY: 2 }}>
              <ListItemIcon sx={{ minWidth: 36, marginTop: 0.5 }}>
                <CbhIcon type="checkmark" size="xLarge" />
              </ListItemIcon>
              <ListItemText
                disableTypography
                primary={
                  <Text variant="body1">Work at least 7 shifts between March 15 and April 15</Text>
                }
              />
            </ListItem>
            <ListItem disableGutters sx={{ alignItems: "flex-start", paddingY: 2 }}>
              <ListItemIcon sx={{ minWidth: 36, marginTop: 0.5 }}>
                <CbhIcon type="checkmark" size="xLarge" />
              </ListItemIcon>
              <ListItemText
                disableTypography
                primary={
                  <Text variant="body1">Have zero no-call-no-show (NCNS) during this period</Text>
                }
              />
            </ListItem>
          </List>
        </Stack>

        <Text
          variant="caption"
          sx={(theme) => ({
            textAlign: "center",
            color: theme.palette.text.secondary,
            marginTop: 8,
          })}
        >
          <TextButton
            size="small"
            sx={{ color: "inherit", textDecoration: "underline", verticalAlign: "baseline" }}
            onClick={async () => {
              await openArticle(concordeScholarshipTermsSupportLink);
            }}
          >
            Terms & Conditions
          </TextButton>{" "}
          apply.
        </Text>
      </DialogContent>
    </BottomSheet>
  );
}
